<template>
  <div class="input-group pb-3">
    <RouterLink :to="{ name: 'labels-item' }" class="btn btn-outline-secondary">
      <font-awesome-icon :icon="['fas', 'chevron-left']" />
      {{ $t('action.back') }}
    </RouterLink>
    <button class="btn btn-outline-secondary" @click="refresh()" :disabled="isLoading">
      <div v-if="isLoading" class="spinner-border spinner-border-sm"></div>
      <font-awesome-icon v-else :icon="['fas', 'rotate']" />
      <span class="d-none d-sm-inline">{{ $t('action.refresh') }}</span>
    </button>
    <input type="search" class="form-control" v-model="itemFilter" :placeholder="$t('field.generic.item')" :disabled="isLoading">
    <input type="search" class="form-control" v-model="lotFilter" :placeholder="$t('field.masterdata.lot')" :disabled="isLoading">
    <button class="btn btn-outline-secondary" @click="resetFilter()" :disabled="isLoading" v-if="itemFilter || lotFilter || filterWord">
      <font-awesome-icon :icon="['fas', 'filter-slash']" />
      {{ runtimeTranscription ?? filterWord }}
    </button>
  </div>

  <div v-if="error" class="alert alert-danger">
    {{ error }}
  </div>
  <div v-else-if="filteredList" id="content-page-table" class="table-fix-head" :style="fixHeadCalculatedStyle">
    <table v-if="filteredList.length > 0" class="table table-hover table-sm table-fit align-middle">
      <thead>
        <tr>
          <th></th>
          <th v-if="selectedSiteId === 0">{{ $t('global.site') }}</th>
          <th>
            {{ $t('field.generic.item') }}
            <font-awesome-icon :icon="['fas', 'sort-up']" />
          </th>
          <th class="d-none d-sm-table-cell">{{ $t('field.masterdata.lot') }}</th>
          <th class="d-none d-sm-table-cell" v-if="hasBestBeforeOnAnyLine">{{ $t('field.masterdata.bestBefore') }}</th>
          <th class="d-none d-sm-table-cell" v-if="hasExpiryOnAnyLine">{{ $t('field.masterdata.expiry') }}</th>
          <th class="d-table-cell d-sm-none">{{ $t('global.propertiesShort') }}</th>
          <th v-if="userSettingsStore.data.showBarcode"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="stock in filteredList" :key="stock.id" @click="toggleSignatureItemSelection(stock)" :class="{ 'is-selected': stock.isStored }">
          <td><input type="checkbox" class="form-check-input" :value="stock.id" :checked="stock.isStored"></td>
          <td v-if="selectedSiteId === 0"><sup class="text-secondary">{{ stock.site.code }}&nbsp;</sup>{{ stock.site.name }}</td>
          <td><ItemField :item="stock.item" /></td>
          <td class="d-none d-sm-table-cell"><LotField :item="stock.item" :lot="stock.lot" /></td>
          <td class="d-none d-sm-table-cell text-center" v-if="hasBestBeforeOnAnyLine"><BestBeforeField :stock="stock" /></td>
          <td class="d-none d-sm-table-cell text-center" v-if="hasExpiryOnAnyLine"><ExpirationField :stock="stock" /></td>
          <td class="d-table-cell d-sm-none">
            <div v-if="stock.lot"><LotField :item="stock.item" :lot="stock.lot" /></div>
            <div v-if="hasBestBeforeOnAnyLine"><BestBeforeField :stock="stock" /></div>
            <div v-if="hasExpiryOnAnyLine"><ExpirationField :stock="stock" /></div>
          </td>
          <td v-if="userSettingsStore.data.showBarcode" class="bg-white p-1">
            <TradeItemBarcode :itemId="stock.item.id" :itemSignature="stock" :size="'3rem'" />
          </td>
        </tr>
      </tbody>
    </table>
    <span v-else>{{ $t('error.noData') }}</span>
  </div>
  <div v-else class="loading">
    {{ $t('status.loadingEllipsis') }}
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import { storeToRefs } from 'pinia';
import { useSessionStore, useStockStore, useUserSettingsStore, useItemLabelsStore } from '@/stores';
import TradeItemBarcode from '@/components/diagram/TradeItemBarcode.vue';
import { BestBeforeField, ExpirationField, LotField, ItemField } from "@/components/fields";
import FrontendStock from "@/models/frontend/Stock";
import { compareOptionalDate, compareOptionalString } from "@/services";
import IItemWithSignatureRaw from "@/models/IItemWithSignatureRaw";

type SelectableStock = FrontendStock &
{
  isStored: boolean;
}

const userSettingsStore = useUserSettingsStore();
const itemLabelsStore = useItemLabelsStore();

const sessionStore = useSessionStore();
const { selectedSiteId } = storeToRefs(sessionStore);

const stockStore = useStockStore();
const { data, error } = storeToRefs(stockStore);
const isLoading = ref(false);
const sort = ref(false);
const baseSortedList = ref<SelectableStock[]>([]);
const itemFilter = ref("");
const lotFilter = ref("");
const offsetTop = ref(0);

const runtimeTranscription = ref(null as string | null);
const filterWord = ref(null as string | null);
const speechVoices = ref([] as SpeechSynthesisVoice[]);

onMounted(async () => {
  await refresh();
  // this is a workaround for the speechSynthesis.getVoices() not being available immediately
  if ('speechSynthesis' in window) {
    speechVoices.value = window.speechSynthesis.getVoices();
    window.speechSynthesis.onvoiceschanged = function() {
      speechVoices.value = window.speechSynthesis.getVoices();
    };
  }

  offsetTop.value = document.querySelector('#content-page-table')?.getBoundingClientRect().top ?? 0;
});

const fixHeadCalculatedStyle = computed(() => {
  return {
    height: `calc(100vh - ${offsetTop.value}px)`,
  };
});

async function refresh() {
  error.value = "";
  isLoading.value = true;

  try {
    await stockStore.fetchAll();
  } finally {
    isLoading.value = false;
  }
}

const resetFilter = () => {
  filterWord.value = null;
  itemFilter.value = "";
  lotFilter.value = "";
}

const isStored = (stock: IItemWithSignatureRaw): boolean =>{
  return itemLabelsStore.isStored(stock);
}

const toggleSignatureItemSelection = (item: SelectableStock) => {
  if (itemLabelsStore.isStored(item)) {
    itemLabelsStore.removeSignatureItem(item);
  } else {
    itemLabelsStore.addSignatureItem(item);
  }
};

const selectableStocks = computed(() => {
  return data.value.map((stock) => {
    var selectableStock = stock as SelectableStock;
    selectableStock.isStored = isStored(stock);
    return stock;
  });
});

const hasBestBeforeOnAnyLine = computed(() => {
  return data.value !== undefined && data.value.findIndex((x) => !!x.bestBefore) !== -1;
});

const hasExpiryOnAnyLine = computed(() => {
  return data.value !== undefined && data.value.findIndex((x) => !!x.expiryAsDate) !== -1;
});

const sortedList = computed(() => {
  if (sort.value) {
    return baseSortedList.value
  }
  else {
    return [...selectableStocks.value].sort((a, b) =>
      a.item.name.localeCompare(b.item.name) ||
      compareOptionalDate(a.expiryAsDate, b.expiryAsDate) ||
      compareOptionalDate(a.bestBeforeAsDate, b.bestBeforeAsDate) ||
      compareOptionalString(a.lot, b.lot)
    );
  }
});

const filteredList = computed(() => {
  let words = [] as string[];
  if (filterWord.value !== null) {
    words = filterWord?.value.toLocaleLowerCase().replace(/[^\wäöü\s]/g, '').split(' ');
  }

  return sortedList.value.filter((item) => {
    return (
      item.item.requiresLabel &&
      (item.item.name.toLowerCase().indexOf(itemFilter.value.toLowerCase()) != -1 || item.item.code.toLowerCase().indexOf(itemFilter.value.toLowerCase()) != -1) &&
      (!lotFilter.value || (item.lot && item.lot.toLowerCase().indexOf(lotFilter.value.toLowerCase()) != -1)) &&
      (words.length === 0 || words.some((element) => item.item.name.toLocaleLowerCase().split(' ').includes(element)))
    );
  }) as SelectableStock[];
});
</script>

<style scoped type="scss">
.is-selected > td {
  background-color: var(--mel-row-filled-bg) !important;
}
</style>
